/**
 * Forbidden (/403) — reason-aware access denied page.
 *
 * Replaces the generic Unauthorized page for the new RBAC layer. Reads
 * `?reason=...` and `?perm=...` query params (set by RequireAccess) and
 * renders an actionable message:
 *   - reason=no_permission → "you lack X; ask your manager"
 *   - reason=off_shift     → "you're off-shift; next window: ..."
 *   - default              → generic 403
 *
 * The page never reveals information about other users; the server-side
 * `me/access` endpoint is the only source of truth.
 */

import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Alert,
  Chip,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import HomeIcon from '@mui/icons-material/Home';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  PERMISSION_CATALOG,
  PermissionMeta,
} from '../constants/permissionCatalog';
import { useAccess } from '../hooks/useAccess';

const Forbidden: React.FC = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const reason = params.get('reason') || 'no_permission';
  const permParam = params.get('perm') || '';
  const perms = permParam.split(',').filter(Boolean);

  const { user, shift, refresh } = useAccess();

  const friendlyName = (code: string) =>
    (PERMISSION_CATALOG as Record<string, PermissionMeta>)[code]?.nameFa || code;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', p: 2 }}>
      <Card sx={{ maxWidth: 560, width: '100%' }}>
        <CardContent>
          <Stack spacing={2} alignItems="center">
            <LockIcon sx={{ fontSize: 64, color: 'error.main' }} />
            <Typography variant="h5" fontWeight={700}>دسترسی غیرمجاز (۴۰۳)</Typography>

            {reason === 'off_shift' ? (
              <Alert severity="warning" sx={{ width: '100%' }} icon={<AccessTimeIcon />}>
                <Typography variant="body2">
                  شما در حال حاضر <strong>خارج از شیفت</strong> هستید.
                  این عمل نیاز به شیفت فعال یا یک <strong>استثناء</strong> از مدیر دارد.
                </Typography>
                {shift.status && (
                  <Box sx={{ mt: 1 }}>
                    <Chip size="small" label={`وضعیت فعلی: ${shift.status}`} />
                  </Box>
                )}
                {perms.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      دسترسی محدود شده: <code>{friendlyName(perms[0])}</code>
                    </Typography>
                  </Box>
                )}
              </Alert>
            ) : (
              <Alert severity="error" sx={{ width: '100%' }}>
                <Typography variant="body2">
                  این بخش به دسترسی‌هایی نیاز دارد که شما ندارید.
                </Typography>
                {perms.length > 0 && (
                  <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {perms.map((p) => (
                      <Chip key={p} size="small" label={friendlyName(p)} />
                    ))}
                  </Box>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  برای دسترسی، با مدیر سیستم تماس بگیرید.
                </Typography>
              </Alert>
            )}

            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
                بازگشت
              </Button>
              <Button startIcon={<HomeIcon />} variant="contained" onClick={() => navigate('/')}>
                داشبورد
              </Button>
              <Button onClick={() => refresh()}>
                به‌روزرسانی دسترسی‌ها
              </Button>
            </Stack>

            {user.id && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
                وارد شده به‌عنوان: {user.name || user.email}
                {user.isSuperAdmin && ' · سوپر-ادمین'}
              </Typography>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Forbidden;
